import React from "react";
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import {
    ThemeProvider,
    StyledEngineProvider,
    createTheme,
    responsiveFontSizes,
    PaletteOptions,
    SimplePaletteColorOptions,
    Palette,
} from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-data-grid-pro";
import { ViewportProvider } from "./components/common/utils/useCurrentWidth";
import createPalette from "@mui/material/styles/createPalette";
import { hexToRgb } from "./components/common/utils/utils";
import GlobalStyles from "@mui/material/GlobalStyles";
import axios from "axios";

const baseUrl: string =
    document.getElementsByTagName("base")[0].getAttribute("href") ?? "";
const root = createRoot(document.getElementById("root")!);

LicenseInfo.setLicenseKey(
    "e3e8c33e1d2ab5dacb81ddfa30e7f5e9Tz02NDMyNixFPTE3MTMwMzE0NTA5MzUsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI="
);

const colors: Palette = createPalette({
    primary: {
        main: "#4E4F92",
        light: "#6667AB",
        dark: "#353761",
        contrastText: "rgba(255, 255, 255, 0.5)",
    },
    secondary: {
        main: "#31BCDB",
        dark: "#11A8D8",
        contrastText: "#FFFFFF",
    },
    success: {
        main: "#65DEA4",
        dark: "#0DC76E",
        contrastText: "#FFFFFF",
    },
    error: {
        main: "#FB5A5A",
        light: "#FF7179",
    },
    warning: {
        main: "#FB805A",
        light: "#FFB967",
    },
    gradient: {
        main: "#5E41C7",
        light: "#A3A2CE",
        dark: "#1B1C49",
    },
});

const fontFamily =
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif !important;';

const theme = createTheme({
    palette: colors,
    typography: {
        fontFamily,
    },
    components: {
        MuiTooltip: {
            styleOverrides: {
                popper: {
                    overflow: "hidden",
                    '&[data-popper-placement*="bottom"]': {
                        ".MuiTooltip-tooltip": {
                            borderRadius: "36px",
                        },
                    },
                    '&[data-popper-placement*="bottom-start"]': {
                        left: "32px !important",
                        ".MuiTooltip-tooltip": {
                            borderRadius: "0px 36px 36px 36px",
                            ".MuiTooltip-arrow": {
                                left: "-19px !important",
                                bottom: "-12px !important",
                            },
                        },
                    },
                    '&[data-popper-placement*="top"]': {
                        ".MuiTooltip-tooltip": {
                            borderRadius: "36px",
                        },
                    },
                    '&[data-popper-placement*="left"]': {
                        ".MuiTooltip-tooltip": {
                            borderRadius: "36px",
                            // ".MuiTooltip-arrow": {
                            //     left: "-15px !important",
                            //     bottom: "-12px !important",
                            // },
                        },
                    },
                },
                tooltip: {
                    backgroundColor: colors.gradient?.main,
                    color: "white",
                    maxWidth: 160,
                    padding: "16px",
                    paddingLeft: "24px",
                    transition: "none !important",
                },
                arrow: {
                    color: colors.gradient?.main,
                    transform: "none !important",
                    left: "-19px !important",
                    width: "2rem !important",
                    height: "1.5rem !important",
                    marginTop: "-1.4rem !important",
                },
                // tooltipPlacementBottom: {
                //     borderRadius: "0px 36px 36px 36px",
                // },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    paddingLeft: "40px !important",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: "18px",
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: "44px",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    color: colors.primary.main,
                    margin: 2,
                    backgroundColor: "#EBEAF4",
                    ".MuiSvgIcon-root": {
                        color: colors.primary.main,
                        fontSize: "14px",
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    ":hover": {
                        boxShadow: `4px 4px 10px 0px rgba(${hexToRgb(
                            colors.gradient?.main!,
                            "string"
                        )}, 0.85)`,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                track: {
                    height: "5px",
                    marginTop: "5px",
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: colors.primary.main,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: "10pt",
                    color: colors.primary.main,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    zIndex: 1,
                    fontWeight: "bold",
                    color: colors.primary.main,
                    textTransform: "none",
                },
                selected: {
                    color: "white!important",
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    background: `rgba(${hexToRgb(
                        colors.primary.main,
                        "string"
                    )}, 0.2)`,
                    borderRadius: "0px 24px 24px 0px",
                },
                indicator: {
                    background: "none",
                    top: 0,
                    height: "100%",
                    width: "100%",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    boxShadow: "none",
                    borderRadius: "30px",
                    paddingLeft: "16px !important",
                    paddingRight: "16px !important",
                    fontSize: "16px",
                    fontWeight: "bold",
                    lineHeight: "18px",
                    "&.Mui-disabled": {
                        opacity: "0.5",
                        "&.MuiButton-textSecondary": {
                            color: colors.secondary.dark,
                        },
                        "&.MuiButton-containedSecondary": {
                            color: colors.secondary.dark,
                            backgroundColor: `rgba(${hexToRgb(
                                colors.secondary.main,
                                "string"
                            )}, 0.25)`,
                        },
                        "&.MuiButton-textPrimary": {
                            color: colors.primary.main,
                        },
                    },
                },
                textSecondary: {
                    color: colors.secondary.dark,
                },
            },
        },
        MuiFab: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    boxShadow: "none",
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    letterSpacing: "0px",
                    "& .superscript": {
                        verticalAlign: "super",
                        fontSize: "8px",
                    },
                    // color: colors.primary.main,
                },
                h1: {
                    letterSpacing: "normal",
                    fontFamily,
                },
                h2: {
                    letterSpacing: "normal",
                    fontFamily,
                },
                h3: {
                    letterSpacing: "normal",
                    fontFamily,
                },
                h4: {
                    letterSpacing: "normal",
                    fontFamily,
                },
                h5: {
                    letterSpacing: "normal",
                    fontFamily,
                },
                h6: {
                    letterSpacing: "normal",
                    fontFamily,
                },
            },
        },
    },
});

axios.interceptors.response.use((response) => {
    if (
        typeof response.data === "string" &&
        response.data.startsWith("\n<head>\n")
    ) {
        window.location.href = "/Account/Login?timeout=true";
    }
    return response;
});

root.render(
    <Provider store={store}>
        <ViewportProvider>
            <BrowserRouter basename={baseUrl}>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={responsiveFontSizes(theme)}>
                        <GlobalStyles
                            styles={{
                                ".MuiDataGrid-menu": {
                                    ".MuiPaper-root": {
                                        borderTop: "1px solid",
                                        borderColor:
                                            theme.palette.secondary.main,
                                        padding: "8px",
                                        marginTop: "7px",
                                        boxShadow: `0px 3px 1px -2px rgba(${hexToRgb(
                                            theme.palette.secondary.main,
                                            "string"
                                        )},0.2), 0px 2px 2px 0px rgba(${hexToRgb(
                                            theme.palette.secondary.main,
                                            "string"
                                        )},0.14), 0px 1px 5px 0px rgba(${hexToRgb(
                                            theme.palette.secondary.main,
                                            "string"
                                        )},0.12)`,
                                        borderRadius: "0px 0px 4px 4px",
                                    },
                                },
                            }}
                        />
                        <App />
                    </ThemeProvider>
                </StyledEngineProvider>
            </BrowserRouter>
        </ViewportProvider>
    </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
